import React from 'react';
import {
  Box,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Tooltip,
  CircularProgress,
  Stack
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Save as SaveIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as CenterIcon,
  GridOn as GridIcon,
  GridOff as GridOffIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  LockOpen as UnlockIcon,
  Lock as LockIcon
} from '@mui/icons-material';

interface SiteMapToolbarProps {
  editMode: 'view' | 'edit' | 'add';
  setEditMode: (mode: 'view' | 'edit' | 'add') => void;
  showGrid: boolean;
  setShowGrid: (show: boolean) => void;
  snapToGrid: boolean;
  setSnapToGrid: (snap: boolean) => void;
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetView: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onSave: () => void;
  saving: boolean;
}

const SiteMapToolbar: React.FC<SiteMapToolbarProps> = ({
  editMode,
  setEditMode,
  showGrid,
  setShowGrid,
  snapToGrid,
  setSnapToGrid,
  zoom,
  onZoomIn,
  onZoomOut,
  onResetView,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onSave,
  saving
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 1,
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper'
      }}
    >
      {/* Edit Mode Toggle */}
      <ToggleButtonGroup
        value={editMode}
        exclusive
        onChange={(e, value) => value && setEditMode(value)}
        size="small"
      >
        <ToggleButton value="view">
          <Tooltip title="View Mode">
            <ViewIcon />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="edit">
          <Tooltip title="Edit Mode">
            <EditIcon />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="add">
          <Tooltip title="Add Mode">
            <AddIcon />
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>

      <Divider orientation="vertical" flexItem />

      {/* Undo/Redo */}
      {editMode === 'edit' && (
        <>
          <Stack direction="row" spacing={0}>
            <Tooltip title="Undo">
              <span>
                <IconButton
                  size="small"
                  onClick={onUndo}
                  disabled={!canUndo}
                >
                  <UndoIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Redo">
              <span>
                <IconButton
                  size="small"
                  onClick={onRedo}
                  disabled={!canRedo}
                >
                  <RedoIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          <Divider orientation="vertical" flexItem />
        </>
      )}

      {/* Grid Controls */}
      <Stack direction="row" spacing={0}>
        <Tooltip title={showGrid ? "Hide Grid" : "Show Grid"}>
          <IconButton
            size="small"
            onClick={() => setShowGrid(!showGrid)}
          >
            {showGrid ? <GridIcon /> : <GridOffIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title={snapToGrid ? "Disable Snap to Grid" : "Enable Snap to Grid"}>
          <IconButton
            size="small"
            onClick={() => setSnapToGrid(!snapToGrid)}
            disabled={!showGrid}
          >
            {snapToGrid ? <LockIcon /> : <UnlockIcon />}
          </IconButton>
        </Tooltip>
      </Stack>

      <Divider orientation="vertical" flexItem />

      {/* Zoom Controls */}
      <Stack direction="row" spacing={0}>
        <Tooltip title="Zoom In">
          <IconButton
            size="small"
            onClick={onZoomIn}
            disabled={zoom >= 2}
          >
            <ZoomInIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom Out">
          <IconButton
            size="small"
            onClick={onZoomOut}
            disabled={zoom <= 0.5}
          >
            <ZoomOutIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Reset View">
          <IconButton
            size="small"
            onClick={onResetView}
          >
            <CenterIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <Box sx={{ flex: 1 }} />

      {/* Save Button */}
      {editMode === 'edit' && (
        <Tooltip title="Save Changes">
          <span>
            <IconButton
              color="primary"
              onClick={onSave}
              disabled={saving}
            >
              {saving ? <CircularProgress size={20} /> : <SaveIcon />}
            </IconButton>
          </span>
        </Tooltip>
      )}

      {/* Zoom Level Display */}
      <Box sx={{ minWidth: 60, textAlign: 'right', fontSize: '0.875rem', color: 'text.secondary' }}>
        {Math.round(zoom * 100)}%
      </Box>
    </Box>
  );
};

export default SiteMapToolbar;